import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCalendarPlus,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useFetchScheduledEvents } from "../../hooks/events/useFetchScheduledEvents";

const AddEventsModal = ({ isOpen, onClose, onAdd, existingEventIds = [] }) => {
  const [selectedEvents, setSelectedEvents] = useState([]);
  const { data: scheduledEvents, isLoading } = useFetchScheduledEvents();

  // Filter out events already in the league
  const availableEvents = useMemo(() => {
    if (!scheduledEvents) return [];
    const existingSet = new Set(existingEventIds.map(String));
    return scheduledEvents.filter(
      (event) => !existingSet.has(String(event.EventId)),
    );
  }, [scheduledEvents, existingEventIds]);

  const toggleEvent = (eventId) => {
    setSelectedEvents((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId],
    );
  };

  const handleAdd = () => {
    if (selectedEvents.length > 0) {
      onAdd(selectedEvents);
      setSelectedEvents([]);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedEvents([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-dark-grey/75 flex justify-center items-center px-4 py-6 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-light-grey">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-deep-red rounded-lg flex items-center justify-center">
              <FontAwesomeIcon
                icon={faCalendarPlus}
                className="text-white text-sm"
              />
            </div>
            <h3 className="text-lg font-semibold text-dark-grey">Add Events</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-medium-grey hover:text-dark-grey transition-colors"
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="text-center py-8 text-medium-grey">
              Loading events...
            </div>
          ) : availableEvents.length === 0 ? (
            <div className="text-center py-8 text-medium-grey">
              No upcoming events available to add
            </div>
          ) : (
            <div className="space-y-2">
              {availableEvents.map((event) => {
                const isSelected = selectedEvents.includes(
                  String(event.EventId),
                );
                return (
                  <button
                    key={event.EventId}
                    onClick={() => toggleEvent(String(event.EventId))}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? "border-deep-red bg-deep-red/5"
                        : "border-light-grey hover:border-medium-grey"
                    }`}
                  >
                    <div className="text-left">
                      <div className="font-medium text-dark-grey text-sm">
                        {event.Name || event.name}
                      </div>
                      <div className="text-xs text-medium-grey">
                        {new Date(
                          event.DateTime || event.date,
                        ).toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 bg-deep-red rounded-full flex items-center justify-center">
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-white text-xs"
                        />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-light-grey flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 bg-light-grey text-dark-grey rounded-lg font-medium hover:bg-medium-grey/20 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={selectedEvents.length === 0}
            className="flex-1 px-4 py-2 bg-deep-red text-white rounded-lg font-semibold hover:bg-bright-red transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add {selectedEvents.length > 0 ? `(${selectedEvents.length})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEventsModal;
